import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PricingSection } from '@/components/PricingSection';
import { BenefitsSlider } from '@/components/BenefitsSlider';
import { ServiceDetailsSlider } from '@/components/ServiceDetailsSlider';
import { Helmet } from 'react-helmet-async';

export default function TrademarkRegistration({ onOpenPopup }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  const faqs = [
    {
      question: 'How long does trademark filing take?',
      answer: 'The complete process usually takes 8–12 months for final approval, while initial filing is completed within 24–48 hours.',
    },
    {
      question: 'What items can be registered?',
      answer: 'Names, words, logos, symbols, designs, or combinations used to identify products or services can be registered.',
    },
    {
      question: 'Is a trademark necessary?',
      answer: 'Yes, if you want to secure your brand identity and prevent others from using similar marks in the market.',
    },
    {
      question: 'What does the filing cost include?',
      answer: 'Our service covers review, search, preparation, filing, and monitoring. Government fees are separate and paid to the USPTO.',
    },
  ];

  const details = [
    {
      icon: <Check className="h-5 w-5 text-[#E1A334]" />,
      title: 'Comprehensive Trademark Search',
      description: 'We conduct thorough searches of federal and state databases to ensure your trademark is available and doesn\'t conflict with existing marks.',
    },
    {
      icon: <Check className="h-5 w-5 text-[#E1A334]" />,
      title: 'Professional Case Review',
      description: 'Our trademark specialists carefully review your application to improve approval chances and ensure correct classification and description.',
    },
    {
      icon: <Check className="h-5 w-5 text-[#E1A334]" />,
      title: 'USPTO Submission & Tracking',
      description: 'We manage the complete filing process with the United States Patent and Trademark Office and continuously monitor your application status until completion.',
    },
    {
      icon: <Check className="h-5 w-5 text-[#E1A334]" />,
      title: 'Ongoing Support',
      description: 'Get dedicated support from our team throughout the registration process, with updates and guidance every step of the way.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="trademark-registration-page">
      <Helmet>
        <title>Trademark Registration Services - Federal USPTO Filing from $45 | Trademark Law Consulting</title>
        <meta name="description" content="Register your trademark with the USPTO starting at $45. Comprehensive trademark search, expert case review, and dedicated support. Fast 24-48 hour filing. 4 affordable packages available." />
        <meta name="keywords" content="trademark registration, USPTO filing, federal trademark, trademark attorney, brand protection, trademark cost, register trademark online" />
      </Helmet>
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/8112131/pexels-photo-8112131.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-[#0A1C3E] opacity-90" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-[#E1A334] mb-4 block">
                Trademark Registration Service 
              </span>
              <h1 className="text-5xl md:text-6xl font-heading font-bold text-white leading-tight mb-6">
               Secure Your Brand Identity
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                Complete federal trademark filing services designed to protect your business name, logo, and overall brand identity. Begin today with professional support at every stage of the process.
              </p>
              <Button
                onClick={onOpenPopup}
                data-testid="hero-register-button"
                className="bg-[#E1A334] hover:bg-[#C4912E] text-white font-semibold rounded-full px-8 py-6 text-lg shadow-xl transition-all duration-300"
              >
               Register Your Trademark
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-2xl border border-slate-200/20">
                <img
                  src="/assets/img/trademark-registration.webp"
                  alt="Trademark Registration"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Why File Your Trademark?
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Protect your brand and unlock stronger business advantages
            </p>
          </motion.div>

          <BenefitsSlider />
        </div>
      </section>

      {/* Info Section - Image Right, Content Left */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-[#E1A334] mb-4 block">
               Our Expertise 
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
               Expert Trademark Filing Services
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Complete federal trademark filing solutions to secure your business name, logo, and brand identity. Start today with guided support at every step.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Our skilled professionals understand the detailed requirements involved in protecting intellectual property. We ensure your application is prepared correctly, submitted on time, and tracked throughout the entire process for smooth handling.
              </p>
              <Button
                onClick={onOpenPopup}
                data-testid="info-section-button"
                className="bg-[#E1A334] hover:bg-[#C4912E] text-white font-semibold rounded-full px-8 py-4 shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-xl border border-slate-200">
                <img
                  src="/assets/img/about-tm-reg.webp"
                  alt="Trademark Registration Process"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Detailed Info Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-[#E1A334] mb-4 block">
             Full-Service Package
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              What You Receive With Our Service
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              End-to-end assistance and expert direction throughout your filing journey
            </p>
          </motion.div>

          <ServiceDetailsSlider details={details} />
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection onOpenPopup={onOpenPopup} />

      {/* FAQ Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Important Information About Trademark Filing
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
              >
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-[#0A1C3E]">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6">
              Start Your Filing Today
            </h2>
            <p className="text-[#E1A334]/80 text-lg max-w-2xl mx-auto mb-8">
              Don’t delay protecting your brand identity. Begin now with our simple and guided trademark filing process.
            </p>
            <Button
              onClick={onOpenPopup}
              data-testid="cta-register-button"
              className="bg-white text-[#0A1C3E] hover:bg-[#E1A334] hover:text-white font-semibold rounded-full px-8 py-6 text-lg shadow-xl transition-all duration-300"
            >
              Register My Trademark
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
